import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './components/Toast';
import useOrganizationColors from './hooks/useOrganizationColors';
import Navigation from './components/Navigation';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SetPassword from './pages/SetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Scanner from './pages/Scanner';
import TrainingLog from './pages/TrainingLog';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import SuperAdmin from './pages/SuperAdmin';
import SystemSetup from './pages/SystemSetup';
import LandingPage from './pages/LandingPage';
import PricingWidget from './pages/PricingWidget';
import PricingSuccess from './pages/PricingSuccess';
import PricingCancel from './pages/PricingCancel';
import SubscriptionCheckout from './components/SubscriptionCheckout';
import DemoExpired from './pages/DemoExpired';
import Calendar from './pages/Calendar';
import CalendarEmbed from './pages/CalendarEmbed';
import StatsEmbed from './pages/StatsEmbed';
import ClubOperations from './pages/ClubOperations';
import Merkeskyting from './pages/Merkeskyting';
import WeaponScanner from './pages/WeaponScanner';
import WeaponLoanForm from './pages/WeaponLoanForm';
import WeaponLoanView from './pages/WeaponLoanView';
import SignageView from './pages/SignageView';

import './App.css';
import { Toaster as SonnerToaster } from 'sonner';
import BirthdayGreeting from './components/BirthdayGreeting';

// Protected Route Component
const ProtectedRoute = ({ children, requireAuth = true, roles = [] }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  
  if (requireAuth && !user) return <Navigate to="/login" />;
  
  if (roles.length > 0 && !roles.includes(user?.role)) {
    return <Navigate to="/" />;
  }
  
  return children;
};

// Color-aware wrapper component
const ColorAwareWrapper = ({ children }) => {
  useOrganizationColors(); // This will update CSS variables when user/organization changes
  return <>{children}</>;
};


// App Layout
const AppLayout = ({ children }) => {
  const [appVersion, setAppVersion] = React.useState("dev");
  const currentYear = new Date().getFullYear();

  React.useEffect(() => {
    try {
      const v =
        document
          ?.querySelector('meta[name="app-version"]')
          ?.getAttribute("content");

      if (v && v.trim() && v !== "%REACT_APP_GIT_VERSION%") {
        setAppVersion(v.trim());
        return;
      }
      // Fallback: use REACT_APP_VERSION env if set (baked at build time), else package.json version
      const envVersion = process.env.REACT_APP_VERSION;
      if (envVersion && envVersion.trim()) {
        setAppVersion(`v${envVersion.trim()}`);
        return;
      }
      // Final fallback: package.json version imported at build time
      // eslint-disable-next-line global-require
      const pkg = require("../package.json");
      if (pkg && pkg.version) {
        setAppVersion(`v${pkg.version}`);
      }
    } catch {
      // keep default
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <ColorAwareWrapper>
        <BirthdayGreeting />
        <Navigation />
        <main className="container mx-auto px-4 py-8 pb-20 xl:pb-8 flex-1">
          {children}
        </main>
      </ColorAwareWrapper>

      <footer className="bg-gray-800/50 border-t border-gray-700/50 py-4 px-4">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center text-xs text-gray-400">
            <div className="mb-2 md:mb-0 flex items-center gap-2">
              <span className="text-gray-500">© {currentYear} </span>
              <a
                href="https://aktivlogg.no"
                target="_blank"
                rel="noopener"
                className="text-gray-500 hover:text-gray-300 transition-colors"
              >
                Aktivlogg
              </a>

              <span className="text-gray-600">•</span>
              <span className="text-gray-500" data-testid="app-version">{appVersion}</span>
            </div>

            <div className="flex space-x-4">
              <a
                href="https://aktivlogg.no/personvernerklaering-2"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-300 transition-colors"
              >
                Personvernerklæring
              </a>
              <span className="text-gray-600">•</span>
              <a
                href="https://aktivlogg.no/betingelser/"
                target="_blank"
                rel="noopener"
                className="hover:text-gray-300 transition-colors"
              >
                Salgs- og leveringsbetingelser
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

/*
// App Layout
const AppLayout = ({ children }) => (
  <div className="min-h-screen bg-gray-900 flex flex-col">
    <ColorAwareWrapper>
      <Navigation />
      <main className="container mx-auto px-4 py-8 pb-20 xl:pb-8 flex-1">
        {children}
      </main>
    </ColorAwareWrapper>
    <footer className="bg-gray-800/50 border-t border-gray-700/50 py-4 px-4">
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center text-xs text-gray-400">
          <div className="mb-2 md:mb-0">
            <span className="text-gray-500">© 2025 </span>
            <a 
              href="https://aktivlogg.no" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-300 transition-colors"
            >
              Aktivlogg
            </a>
          </div>
          <div className="flex space-x-4">
            <a 
              href="https://aktivlogg.no/personvernerklaering-2" 
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-300 transition-colors"
            >
              Personvernerklæring
            </a>
            <span className="text-gray-600">•</span>
            <a 
              href="https://aktivlogg.no/betingelser/" 
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-300 transition-colors"
            >
              Salgs- og leveringsbetingelser
            </a>
          </div>
        </div>
      </div>
    </footer>
  </div>
);*/

// Main App Routes
const AppRoutes = () => {
  const { needsSetup } = useAuth();
  
  if (needsSetup) {
    return <SystemSetup />;
  }
  
  return (
    <Routes>
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/public/signage/:orgId/:token" element={<SignageView />} />
      <Route path="/pricing-widget" element={<PricingWidget />} />
      <Route path="/pricing/success" element={<PricingSuccess />} />
      <Route path="/pricing/cancel" element={<PricingCancel />} />
      <Route path="/login" element={<Login />} />
      <Route path="/demo-expired" element={<DemoExpired />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register/:orgSlug" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password/:token" element={<SetPassword />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      
      {/* Public calendar embed (no auth required) */}
      <Route path="/embed/calendar/:orgId" element={<CalendarEmbed />} />
      <Route path="/embed/stats/:orgId" element={<StatsEmbed />} />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Home />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/scanner"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Scanner />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/log"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TrainingLog />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Profile />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Calendar />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SubscriptionCheckout />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={['admin', 'org_admin', 'range_officer']}>
            <AppLayout>
              <Admin />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute roles={['super_admin']}>
            <AppLayout>
              <SuperAdmin />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/club-operations"
        element={
          <ProtectedRoute roles={['admin', 'org_admin', 'range_officer']}>
            <AppLayout>
              <ClubOperations />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/merkeskyting"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Merkeskyting />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/weapon-scanner"
        element={
          <ProtectedRoute>
            <WeaponScanner />
          </ProtectedRoute>
        }
      />

      <Route
        path="/weapon-loan"
        element={
          <ProtectedRoute>
            <WeaponLoanForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/weapon-loan/:loanId"
        element={
          <ProtectedRoute>
            <WeaponLoanView />
          </ProtectedRoute>
        }
      />
      
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LanguageProvider>
          <ThemeProvider>
            <ToastProvider>
              <SonnerToaster position="top-center" theme="dark" richColors closeButton />
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </ToastProvider>
          </ThemeProvider>
        </LanguageProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;